import { Brand, Frame, Img, DeckViewer, DeckPrint } from "../headliner/DeckShell";
import { CinematicCover, CINE_ASSETS } from "../headliner/CinematicCover";
import { WhyNow } from "../headliner/HeadlinerDeck";
import { Wordmark } from "../headliner/brands";
import "../headliner/headliner.css";
import "../lawpartner/law.css";
import "../buildpartner/build.css";
import "../visiondeck/vision-deck.css";
import "../masterdeck/master.css";
import "./delivery.css";

/* Delivery Partner-deck — «Built with Unite & <mediepartner>». Et deck laget TIL dem, ikke OM dem (brukerens review): ambisjon → why → Bergen-
   muligheten → første utgave setter standarden → hva vi bygger → hvorfor delivery er ett system → fire roller (eierskap, ikke bestilling)
   → Why Unite → Why <mediepartner> → commercial model m/ gates → hva de får → hva vi ber om NÅ → 30 dager → commercial guardrails → worth your
   best people → close. Statusmodell som ellers: Kevin proposed/availability confirmed; Grieghallen offer received; Unite/mediepartner proposed.
   Mediepartneren velges av mottakerfeltet: «Unite & Apriil» (default) eller «Unite & Cure» — Unite er alltid produksjonspartner. */
const UNITE = "/logos/unite.svg", GRIEG = "/logos/grieghallen.svg";
const MEDIA = {
  apriil: { name: "Apriil", logo: "/logos/apriil.svg", cls: "apriil" },
  cure: { name: "Cure", logo: "/logos/cure.svg", cls: "cure" },
};
export const mediaOf = (mottaker) => (/cure/i.test(mottaker || "") ? MEDIA.cure : MEDIA.apriil);
const recipientOf = (mottaker) => mottaker || `Unite & ${mediaOf(mottaker).name}`;
const Mask = ({ logo, name, cls = "" }) => <i className={`mask ${cls}`} style={{ "--m": `url(${logo})` }} aria-label={name} />;

/* 01 The ambition */
const Cover = ({ mottaker }) => {
  const mp = mediaOf(mottaker);
  return (
    <CinematicCover
      className="ms-cover vd-cover-tight"
      testid="dl-cover"
      lockup={<p className="bd-lockup" data-testid="dl-lockup"><Brand size={19} /><i>×</i><Wordmark name="Unite" h={26} /><i className="amp">&amp;</i><Wordmark name={mp.name} h={26} /></p>}
      label={<><b>Delivery Partnership</b>Prepared for {recipientOf(mottaker)} · Private &amp; confidential</>}
      title={["Built in Bergen.", "Designed for Norway."]}
      sub={<><span className="l1">One day. One room. 700 of Norway's leaders.</span>A national AI arena — <b>built with {recipientOf(mottaker)}</b>.</>}
      index={[
        ["Date", "13 April 2027"],
        ["Venue", "Grieghallen, Bergen"],
        ["Prepared for", recipientOf(mottaker), "prep", "dl-cover-prep"],
      ]}
    />
  );
};

/* 03 The Bergen opportunity */
const BERGEN = [
  ["Businesses", "World-class maritime, energy, finance, media and technology companies"],
  ["Capital", "Owners, investors and institutions with national reach"],
  ["Knowledge", "Universities, research groups, founders and technical communities"],
  ["The gap", "No annual AI business arena with national gravity — yet"],
];

const BergenOpportunity = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The Bergen opportunity" mottaker={mottaker} className="hd-bergen hd-light">
    <Img className="hd-bergen-img" src="/images/bergen-night.webp" alt="Bergen at night, seen from Fløyen" draggable={false} />
    <span className="hd-credit">Photo · Svein-Magne Tunli · CC BY-SA 4.0</span>
    <h2 className="hd-h2" data-testid="dl-bergen-h">This can become<br />bigger than an event.</h2>
    <p className="hd-bergen-sub" data-testid="dl-bergen-sub">Bergen has world-class businesses, capital, technology environments and academic institutions — but no annual AI business arena with national gravity. Norwegian AI Summit is an opportunity to change that.</p>
    <ul className="hd-ledger" data-testid="dl-bergen-points">
      {BERGEN.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}
    </ul>
    <p className="hd-bergen-foot" data-testid="dl-bergen-foot"><b>Bergen is the place. Norway is the market.</b> Doing that requires more than a venue and a speaker line-up — it requires world-class production, communications, distribution and execution from day one.</p>
  </Frame>
);

/* 04 The first edition sets the standard */
const FIRST = [
  ["The room", "700 selected leaders, not footfall — the audience is the product."],
  ["The brand", "An identity and launch system that reads as an institution, not a first edition."],
  ["The production", "A stage and a broadcast experience worthy of the audience."],
  ["The programme", "Global-calibre voices, prepared sessions, one designed day."],
  ["The distribution", "A story that starts months before 13 April and continues after it."],
];

const FirstEdition = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The first edition sets the standard" mottaker={mottaker} className="ms-coal hd-light">
    <h2 className="hd-h2" data-testid="dl-first-h">There is no second chance<br />to launch the first<br />Norwegian AI Summit.</h2>
    <p className="ms-coal-sub" data-testid="dl-first-sub">If Bergen is going to matter on the AI map, the first edition has to feel as if it has existed for five years. The room, the brand, the production, the programme and the distribution must tell the same story.</p>
    <div className="hd-exec-grid ms-coal-grid" data-testid="dl-first-points">
      {FIRST.map(([t, d]) => <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>)}
    </div>
    <p className="ms-coal-key" data-testid="dl-first-key">This belongs on the national calendar.</p>
    <p className="hd-exec-foot" data-testid="dl-first-foot">That is why we want to build it with Unite and {mediaOf(mottaker).name}.</p>
  </Frame>
);

/* 05 What we are building — fakta + en produsert dag */
const FACTS = [
  ["The room", "700 seats, invitation-led — leadership, technology, capital and policy in one hall. No parallel tracks."],
  ["The place", "Grieghallen, Bergen — main hall, foyer and Summit Dinner. 13 April 2027. Venue offer received."],
  ["The headliner", "Kevin O'Leary — proposed opening keynote, 2.5 hours on site. Availability confirmed; not yet contracted."],
  ["The programme", "Leadership · Frontier · Rules · Build — four pillars, every session prepared, briefed and moderated."],
];
const DAG = [
  ["09:00", "Arrival · registration · foyer"], ["09:30", "Opening keynote · Kevin O'Leary (proposed)", true], ["10:20", "The Conversation"], ["11:30", "Frontier Keynote"],
  ["12:00", "Rules Segment"], ["13:00", "AI Experience · The Build Lab · lunch", true], ["14:30", "Live build", true], ["16:00", "Closing keynote"], ["18:30", "Summit Dinner", true],
];

const Building = ({ nr, mottaker }) => (
  <Frame nr={nr} label="What we are building" mottaker={mottaker} className="ms-prog hd-day hd-light">
    <h2 className="hd-h2" data-testid="dl-build-h">One room. One programme.<br />700 of Norway's leaders.</h2>
    <ol className="ms-pillars" data-testid="dl-build-facts">
      {FACTS.map(([t, d], i) => <li key={t}><span className="k">0{i + 1}</span><span className="t">{t}</span><span className="d">{d}</span></li>)}
    </ol>
    <ol className="hd-timeline" data-testid="dl-timeline">
      {DAG.map(([t, l, hi]) => <li key={t} className={hi ? "hi" : ""}><span className="t">{t}</span><i className="pt" /><span className="l">{l}</span></li>)}
    </ol>
    <p className="hd-dayfoot" data-testid="dl-build-foot">Highlighted: the production-heavy moments — the opening, the experience floor, the live build and the dinner. Every hour is produced. Times are proposed and set with the programme.</p>
  </Frame>
);

/* 06 Why delivery matters — ett system, ikke tre leverandører */
const SYSTEM = (mp) => [
  ["Brand", `What the summit looks like before anyone has been in the room — identity, launch, announcements. ${mp.name}'s discipline.`],
  ["Experience", "What the room feels like on the day — stage, foyer, hospitality, run-of-show. Unite's discipline."],
  ["Distribution", "What the summit becomes afterwards — clips, sessions, an editorial series, the next edition. Built together."],
];

const WhyDelivery = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Why delivery matters" mottaker={mottaker} className="hd-exec dl-why hd-light">
    <h2 className="hd-h2 big" data-testid="dl-why-h">Brand, experience and distribution<br />must behave as one system.</h2>
    <p className="hd-exec-sub" data-testid="dl-why-sub">A main partner asks two things at once: <b>Is the position worth the money?</b> And <b>do we trust these people to deliver it at the level the deck shows?</b> The second question is answered by the delivery system — not by the founders.</p>
    <div className="hd-exec-grid" data-testid="dl-why-points">
      {SYSTEM(mediaOf(mottaker)).map(([t, d]) => <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>)}
    </div>
    <p className="hd-exec-foot" data-testid="dl-why-foot">Three disciplines, one story. As separate suppliers, the room feels assembled. As one system, it feels inevitable.</p>
  </Frame>
);

/* 07 Four roles. One delivery model. — eierskap til fagområdet, ikke bestilling */
const ROLES = (mp) => [
  ["Norwegian AI Summit", null, "Owns the ambition, the audience, the programme and the commercial platform.", ["Concept and editorial direction", "Programme and speakers", "Partnerships and commercial model", "The 700"], "Owner"],
  ["Unite", UNITE, "Owns how the ambition becomes an experience.", ["Production design and technical production", "Run-of-show and event-day operations", "Hospitality, registration, foyer, dinner", "The participant journey"], "Proposed"],
  [mp.name, mp.logo, "Owns how the ambition becomes visible and relevant.", ["Identity and launch system", "PR, press and announcements", "Campaign, content and distribution", "The year-round editorial platform"], "Proposed"],
  ["Grieghallen", GRIEG, "Provides the physical platform.", ["Main hall and foyer", "Technical infrastructure", "Hospitality and the Summit Dinner", "13 April 2027"], "Offer received"],
];

const Roles = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The roles" mottaker={mottaker} className="dl-roles hd-light">
    <h2 className="hd-h2" data-testid="dl-roles-h">Four roles.<br />One delivery model.</h2>
    <p className="dl-roles-sub">Different roles — equal weight. Each partner owns its discipline without anyone giving up the summit. Norwegian AI Summit does not order; it convenes.</p>
    <ol className="dl-roles-grid" data-testid="dl-roles-grid">
      {ROLES(mediaOf(mottaker)).map(([n, logo, role, items, st]) => (
        <li key={n} className={n === "Grieghallen" ? "venue" : ""}>
          <span className="logo">{logo ? <Mask logo={logo} name={n} cls={n.toLowerCase()} /> : <b>Norwegian AI Summit</b>}</span>
          <span className="role">{role}</span>
          <ul>{items.map((x) => <li key={x}>{x}</li>)}</ul>
          <span className={`st ${st === "Owner" ? "own" : ""}`}>{st}</span>
        </li>
      ))}
    </ol>
  </Frame>
);

/* 08 Why Unite · 09 Why Apriil — samme mal, like tunge */
const SCOPE_UNITE = [
  ["Production design", "The physical product: stage, screen, light, sound, furniture — one visual system with Apriil's identity."],
  ["Run-of-show", "Show calling, speaker movement, technical rehearsal, timing — from 09:00 arrival to 18:30 dinner."],
  ["Experience & hospitality", "Registration, foyer, The Build Lab and partner activations, green room, VIP handling, catering, Summit Dinner."],
  ["Event-day operations", "Staffing, security, logistics and contingencies. The room runs — whatever happens."],
];
const SCOPE_APRIIL = [
  ["Identity & art direction", "Brand identity, visual system and art direction — built to read as an institution from the first announcement."],
  ["Launch system", "Website system, launch key visual, motion language, partner and speaker deck templates — one system, many outputs."],
  ["Campaign & content", "Social templates, paid campaign assets, email graphics, speaker-announcement templates, films and short-form edits."],
  ["PR & distribution", "Launch strategy, press, speaker and partner announcements — flagship clips within 24 hours, an editorial series through the year."],
];

const RoleSlide = ({ nr, mottaker, name, logo, cls, title, lead, scope, foot, testid }) => (
  <Frame nr={nr} label={`Why ${name}`} mottaker={mottaker} className="hd-exec dl-role hd-light">
    <p className="dl-role-logo"><Mask logo={logo} name={name} cls={cls} /><span>{title}</span></p>
    <h2 className="hd-h2 big" data-testid={`${testid}-h`}>{lead}</h2>
    <div className="hd-exec-grid dl-role-grid" data-testid={`${testid}-scope`}>
      {scope.map(([t, d]) => <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>)}
    </div>
    <p className="hd-exec-foot">{foot}</p>
  </Frame>
);

const UniteRole = (p) => (
  <RoleSlide {...p} name="Unite" logo={UNITE} cls="unite" title="Official Production & Experience Partner" testid="dl-unite"
    lead={<>Unite owns how the ambition<br />becomes an experience.</>} scope={SCOPE_UNITE}
    foot="Proposed scope. Designed together from inception — final scope and budget envelope agreed before anything is locked." />
);
const MediaRole = (p) => {
  const mp = mediaOf(p.mottaker);
  return (
    <RoleSlide {...p} name={mp.name} logo={mp.logo} cls={mp.cls} title="Official Brand, Media & Distribution Partner" testid="dl-media"
      lead={<>{mp.name} owns how the ambition<br />becomes visible and relevant.</>} scope={SCOPE_APRIIL}
      foot="Proposed scope — a launch system, not a visual profile. Final scope, rights and budget agreed before anything is locked." />
  );
};

/* 10 The commercial model — partners + seats + gates */
const MODEL = [
  ["The financing stack", "One Presenting Partner at 5 MNOK · strategic, category and ecosystem partners · paid seats and the Summit Dinner. Three sources in roughly equal parts. Positions confirmed by 1 October 2026."],
  ["The gates", "Major commitments follow committed revenue: the headliner is contracted when the Presenting Partner is; full production mobilises when the budget envelope is signed. A contingency reserve from day one."],
  ["Preferred delivery position", "If the summit is realised, the production and communications mandates are yours — fully paid when partner and ticket financing closes."],
  ["Early work, real upside", "Some development is invested or discounted now; full-paid delivery follows the financing. Healthier than «everything for a logo»."],
];

const Commercial = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The commercial model" mottaker={mottaker} className="hd-cant dl-model hd-light">
    <h2 className="hd-h2" data-testid="dl-model-h">Financed by partners and seats.<br />Committed in gates.</h2>
    <ul className="hd-ledger" data-testid="dl-model-points">
      {MODEL.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}
    </ul>
    <p className="hd-cant-foot" data-testid="dl-model-foot">Built with partners. Not sold to sponsors — and not built on undefined risk for the people delivering it.</p>
  </Frame>
);

/* 11 What this gives Unite & Apriil — konkret partner value, ikke «rabatt mot logo» */
const GIVES = (mp) => [
  ["Unite", UNITE, "unite", "Official Production & Experience Partner", ["Category exclusivity", "Visibility in selected event communications", "Norwegian AI Summit as a reference case — with agreed content and assets for case development", "Involvement in designing the experience from inception", "First conversation on the 2028 delivery"]],
  [mp.name, mp.logo, mp.cls, "Official Brand, Media & Distribution Partner", ["Category exclusivity", "A credited role in building the launch platform", "National case rights", "Visibility through selected Norwegian AI Summit communication", "Shaping the year-round editorial and distribution model — and first conversation on 2028"]],
];

const Gives = ({ nr, mottaker }) => (
  <Frame nr={nr} label={`What this gives Unite & ${mediaOf(mottaker).name}`} mottaker={mottaker} className="dl-gives hd-light">
    <h2 className="hd-h2" data-testid="dl-gives-h">What this gives you —<br />beyond the mandate.</h2>
    <div className="dl-gives-grid" data-testid="dl-gives-grid">
      {GIVES(mediaOf(mottaker)).map(([n, logo, cls, title, items]) => (
        <div key={n} data-testid={`dl-gives-${cls}`}>
          <Mask logo={logo} name={n} cls={cls} />
          <span className="t">{title}</span>
          <ul>{items.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      ))}
    </div>
    <p className="bd-foot" data-testid="dl-gives-foot">Delivered with — never «sponsored by». Commercial partners buy positions; delivery partners build the arena and are credited as the ones who did.</p>
  </Frame>
);

/* 12 What we are asking from you now — not / now / then */
const ASK = [
  ["Not", ["A full production commitment", "An open-ended investment of unpaid time", "A signature before scope and economics are real"], "not"],
  ["Now", ["One senior working session", "Agree scope and decision rights", "Produce a realistic budget envelope", "Define the partnership model", "Decide whether we proceed together"], "now"],
  ["Then", ["Formal mandate", "Commercial terms", "Launch"], "then"],
];

const Asking = ({ nr, mottaker }) => (
  <Frame nr={nr} label="What we are asking from you now" mottaker={mottaker} className="dl-asknow hd-light">
    <h2 className="hd-h2" data-testid="dl-ask-h">What we are asking<br />from you now.</h2>
    <p className="dl-asknow-sub" data-testid="dl-ask-sub">Before we go hard after the Presenting Partner, we want the delivery coalition to be real — so the case we present is real. That starts with one decision, not a contract.</p>
    <div className="dl-asknow-grid" data-testid="dl-ask-points">
      {ASK.map(([k, items, cls]) => <div key={k} className={cls}><span className="k">{k}</span><ul>{items.map((x) => <li key={x}>{x}</li>)}</ul></div>)}
    </div>
    <p className="hd-exec-foot" data-testid="dl-ask-foot">We want to build this with you — and we want you to be able to say yes to something specific.</p>
  </Frame>
);

/* 13 The next 30 days */
const DAYS30 = (mp) => [
  ["Senior working session — roles, scope, decision rights", `Norwegian AI Summit · Unite · ${mp.name}`, "Week 1"],
  ["Joint delivery plan — venue to show day", "Unite, with Norwegian AI Summit", "Week 2"],
  ["Budget envelope and expected fee ranges · launch-system scope and rights", `Unite · ${mp.name}`, "Week 2–3"],
  ["Update the Presenting Partner deck — «The team that can deliver it»", `Norwegian AI Summit · ${mp.name}`, "Week 3"],
  ["Meetings with 3–5 major partners", "Norwegian AI Summit, with the delivery team", "Week 3–4"],
  ["Partnership model agreed — mandate, terms, launch", "All", "Week 4"],
];

const Next30 = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The next 30 days" mottaker={mottaker} className="dl-30 hd-light">
    <h2 className="hd-h2" data-testid="dl-30-h">The next 30 days.</h2>
    <p className="dl-30-sub">Scope · budget envelope · responsibilities · partner pitch · timeline. Owners are proposed — we settle them together on Monday.</p>
    <ol className="dl-30-list" data-testid="dl-30-list">
      {DAYS30(mediaOf(mottaker)).map(([t, o, w], i) => <li key={t}><span className="n">0{i + 1}</span><span className="t">{t}</span><span className="o">{o}</span><span className="w">{w}</span></li>)}
    </ol>
  </Frame>
);

/* 14 Commercial guardrails — ingen udefinert risiko, for noen av sidene */
const GUARD = [
  ["Scope before mobilisation", "Scope and economics agreed before full mobilisation. No open-ended scope — on either side."],
  ["Paid work is paid work", "Paid work separated from agreed partnership value. External spend approved against budget."],
  ["Gates before commitments", "No major commitment before the financing gates are met — the headliner, full production and paid media each have one."],
  ["A budget envelope, early", "Phase 1 is priced. A preliminary total budget envelope and expected fee range from Unite before the headliner decision."],
  ["Rights, agreed up front", "Adaptation and editing rights, native files, unlimited use across time, channel and geography, and the right to hand assets to other production partners — in the mandate, not discovered later."],
  ["Three levels of certainty", "Scoped work · provisional estimates · final approval — always labelled, never blurred."],
];

const Guardrails = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Commercial guardrails" mottaker={mottaker} className="hd-cant dl-guard hd-light">
    <h2 className="hd-h2" data-testid="dl-guard-h">Norwegian AI Summit will not ask its delivery partners<br />to carry undefined production risk.</h2>
    <ul className="hd-ledger" data-testid="dl-guard-points">
      {GUARD.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}
    </ul>
  </Frame>
);

/* 15 Why it is worth your best people */
const UPSIDE = [
  ["A reference case", "An international headliner, a live build and a curated 700-seat room in Grieghallen — the kind of case that travels."],
  ["Proximity to 700 leaders", "The people deciding how AI is adopted in Norway — and the partners who pay to be in front of them."],
  ["A long-term annual platform", "Not a one-off production. The first edition sets the standard; the mandate is meant to last."],
  ["Visibility as builders", "Named as the ones building Norway's new AI arena — in every partner deck, in the announcement and in the room."],
];

const Upside = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Why it is worth your best people" mottaker={mottaker} className="hd-exec hd-light">
    <h2 className="hd-h2 big" data-testid="dl-upside-h">Why this is worth<br />your best people.</h2>
    <p className="hd-exec-sub">Beyond the mandate itself: what Unite and {mediaOf(mottaker).name} get from building Norwegian AI Summit from the first edition — and why we are asking for seniority now, before everything is specified.</p>
    <div className="hd-exec-grid" data-testid="dl-upside-points">
      {UPSIDE.map(([t, d]) => <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>)}
    </div>
    <p className="hd-exec-foot">Built with partners. Not sold to sponsors.</p>
  </Frame>
);

/* 16 Let's build the first edition together */
const Close = ({ nr, mottaker }) => {
  const mp = mediaOf(mottaker);
  return (
    <Frame nr={nr} label="Let's build the first edition together" mottaker={mottaker} className="vd-end dl-close hd-light">
      <h2 className="hd-h2" data-testid="dl-close-h">If Bergen is going to matter on the AI map, the first edition has to matter.</h2>
      <p className="vd-end-p" data-testid="dl-close-p">That takes more than founders. It takes the right people around the table.</p>
      <p className="dl-close-lockup" data-testid="dl-close-lockup"><span className="wm">Norwegian AI Summit</span><i>×</i><Mask logo={UNITE} name="Unite" cls="unite" /><i>×</i><Mask logo={mp.logo} name={mp.name} cls={mp.cls} /><i>×</i><Mask logo={GRIEG} name="Grieghallen" cls="grieghallen" /></p>
    <ul className="hd-ledger vd-ledger" data-testid="dl-close-ledger">
      <li><span className="k">Monday</span><span className="v">This conversation. Ambition, roles, the ask.</span></li>
      <li><span className="k">Next week</span><span className="v">Senior working session — delivery plan, budget envelope, decision rights.</span></li>
      <li><span className="k">Within 30 days</span><span className="v">Partnership model agreed. Presenting Partner meetings with the delivery team in the room.</span></li>
      <li><span className="k">13 April 2027</span><span className="v">Grieghallen. One day. One room. Built together.</span></li>
    </ul>
    <p className="vd-end-next" data-testid="dl-close-next"><span className="k">Next step</span>We bring the programme and the partners. You bring the room and the reach.</p>
      <div className="hd-contact vd-contact" data-testid="dl-contact"><span className="n">Martin Kviteberg · CEO, Norwegian AI Summit</span><span className="e">martin@norwegianaisummit.no</span></div>
    </Frame>
  );
};

const RECIPIENT = "Unite & Apriil";
const SLIDES = [Cover, WhyNow, BergenOpportunity, FirstEdition, Building, WhyDelivery, Roles, UniteRole, MediaRole, Commercial, Gives, Asking, Next30, Guardrails, Upside, Close];
const slidesFor = () => SLIDES;

const ASSETS = [...CINE_ASSETS, "/images/bergen-night.webp", UNITE, MEDIA.apriil.logo, MEDIA.cure.logo, GRIEG];

export function DeliveryPrint() {
  return <DeckPrint slides={slidesFor} fallback={RECIPIENT} />;
}

export default function DeliveryDeck({ mottaker = RECIPIENT, publicMode = false }) {
  return <DeckViewer deck="delivery" slides={slidesFor} fallback={mottaker || RECIPIENT} filePrefix="Norwegian-AI-Summit-Delivery-Partnership" assets={ASSETS} publicMode={publicMode} />;
}
